import { R4 } from '@ahryman40k/ts-fhir-types';
import { DependentRelation } from '../contexts/a2i-data-provider';
import { Immunization } from './immunization';

export interface Patient extends R4.IPatient {
  fullName: string;
  displayName: string;
  firstName: string;
  lastName: string;
  immunizations: Array<Immunization>;
  email?: string;
  relation?: 'parent' | 'guardian';
  panoramaUserId?: string;
  hcn?: string;
  isDeceased: boolean;
}

export const parsePatientBundle = (
  bundle: R4.IBundle,
  dependents: Array<DependentRelation>
): Array<Patient> => {
  const patients = bundle.entry
    ?.filter((e) => e.resource!.resourceType === 'Patient')
    .map((e) => parsePatient(e.resource as R4.IPatient, dependents));
  return patients ?? [];
};

export const parsePatient = (
  patient: R4.IPatient,
  dependents: Array<DependentRelation>
): Patient => {
  const code = dependents.find((dependent) => dependent.id === patient.id)?.code;
  const preferredName = patient?.name?.find((name) => name.use === R4.HumanNameUseKind._usual);
  const officialName = patient?.name?.find((name) => name.use === R4.HumanNameUseKind._official);

  return {
    ...patient,
    immunizations: [],
    hcn: patient.identifier?.length ? getNSHcn(patient.identifier) : '',
    panoramaUserId: patient.identifier?.find(
      (id) => id.system && /(prod|uat).id.panorama.source/.test(id.system)
    )?.value,
    fullName: getFullName(officialName, preferredName),
    displayName: getFullName(preferredName, officialName),
    firstName: preferredName?.given?.[0] ?? officialName?.given?.[0] ?? '',
    lastName: preferredName?.family ?? officialName?.family ?? '',
    email: patient?.telecom?.find((telecom) => String(telecom?.system) === 'Email')?.value ?? '',
    relation: code === 'GUARD' ? 'guardian' : code === 'NOK' ? 'parent' : undefined,
    isDeceased: patient?.active === false && !!patient?.deceasedDateTime
  };
};

const getNSHcn = (identifiers: Array<R4.IIdentifier>) => {
  const hcns = identifiers.filter(
    (id) =>
      id.system === 'https://fhir.infoway-inforoute.ca/NamingSystem/ca-ns-patient-healthcare-id'
  );
  if (hcns?.length === 0) return;
  if (hcns?.length === 1) return hcns[0].value;
  if (hcns?.length > 1) return hcns.find((hcn) => hcn.use === 'official')?.value;
};

/**
 * Combines the given and family names.
 * for each field, prefer the primary name and use the fallback if the primary is not available.
 * @param name The primary name entry to use.
 * @param fallback The fallback name entry to use if the primary is not available.
 * @returns The full name as a string, combining given names and family name.
 */
const getFullName = (name?: R4.IHumanName, fallback?: R4.IHumanName) => {
  const names: string[] = [];

  if (name?.given?.length) names.push(...name.given);
  else if (fallback?.given?.length) names.push(...fallback.given);

  if (name?.family) names.push(name.family);
  else if (fallback?.family) names.push(fallback.family);

  return names.join(' ') ?? '';
};
